import type { RouteRecordRaw } from 'vue-router'

import {
  DocumentDataProvider,
  DocumentDetails,
  DocumentList,
  CreateTicketModal,
  EditProspectListModal,
  EditTicketModal,
  EventApplications,
  EventLeads,
  InvitationQuotaCsvMappingForm,
  InvitationQuotaCsvUpload,
  InvitationQuotaViewSwitch,
  PartnerCompanyDetails,
  PartnerCompanyForm,
  ProspectEvent,
  ProspectEvents,
  ProspectList,
  ProspectLists,
  ProspectsCatalog,
  SendProspectListInvitationsModal,
  SendProspectTestEmailModal,
  TicketDataProvider,
  TicketDetailsModal,
} from '@/components'
import AuthorizedArea from '@/components/AuthorizedArea.vue'
import { AttendeeUpload } from '@/components/EventApplications'
import { EventForm } from '@/components/EventsInvites'
import LandingPage from '@/components/Hygraph/LandingPage.vue'
import { DeleteProspectListModal } from '@/components/Prospects'
import {
  CreateSpeaker,
  EditSpeakerEvents,
  EditSpeaker,
  SpeakerDataProvider,
  SpeakerList,
  SpeakerView,
} from '@/components/SpeakerManagement'
import { UserList, EditUser, CreateUser } from '@/components/UserManagement'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { reviewsAgenciesRoutes } from '@/router/reviewsAgenciesRoutes.ts'
import { reviewsProductsRoutes } from '@/router/reviewsProductsRoutes.ts'
import {
  BookingsDataProvider,
  DashboardSection,
  ErrorNotFound,
  ErrorUnauthorized,
  EventDataProvider,
  EventInvitations,
  EventsDataProvider,
  InvitationDataProvider,
  MeetingsContainer,
  MeetingsMyMeetings,
  MeetingsRequests,
  MeetingsDeclined,
  MeetingsConfiguration,
  HrReach,
  MyLeads,
  NetworkIssue,
  NotificationCenter,
  PartnerCompany,
  ProspectsSection,
  SpeakersDataProvider,
  UsersDataProvider,
  VisitorProfiles,
} from '@/views'
import {
  BookingOverview,
  BookingSelection,
  CheckoutOrder,
  FestivalShopLayout,
  ManageOrders,
  OrderConfirmation,
  CategoryProductList,
  ProductDetails,
} from '@/views/FestivalShop'
import KioskDataProvider from '@/views/FestivalShop/DataProviders/KioskDataProvider.vue'
import OrdersProvider from '@/views/FestivalShop/DataProviders/OrdersProvider.vue'
import ProductEditorProvider from '@/views/FestivalShop/DataProviders/ProductEditorProvider.vue'
import ProductsDataProvider from '@/views/FestivalShop/DataProviders/ProductsDataProvider.vue'
import ShopEventDataProvider from '@/views/FestivalShop/DataProviders/ShopEventDataProvider.vue'
import { BoothOrderSummary } from '@/views/PrintViews'

const routes: Readonly<RouteRecordRaw[]> = [
  {
    path: '/',
    component: AuthorizedArea,
    children: [
      {
        path: '',
        name: 'root',
        redirect: { name: 'dashboard' },
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/festival-shop',
        name: 'festivalShop',
        component: FestivalShopLayout,
        meta: {
          isPublic: false,
          fullWidth: true,
          hasSubmenu: true,
        },
        children: [
          {
            path: '',
            name: 'shopBookingSelection',
            props: true,
            component: BookingSelection,
            meta: { title: 'Select booking', cart: false },
          },
          {
            path: ':eventSlug',
            props: true,
            component: ShopEventDataProvider,
            children: [
              {
                path: ':kioskId',
                props: true,
                component: KioskDataProvider,
                children: [
                  {
                    path: '',
                    name: 'shopBookingOverview',
                    props: true,
                    component: BookingOverview,
                    meta: { title: 'Overview', cart: true },
                  },
                  {
                    path: 'manage',
                    props: true,
                    component: OrdersProvider,
                    children: [
                      {
                        path: '',
                        name: 'shopManageOrders',
                        props: true,
                        component: ManageOrders,
                        meta: { title: 'Ordered items', cart: true },
                      },
                      {
                        path: 'edit',
                        props: true,
                        component: ProductEditorProvider,
                        children: [
                          {
                            path: ':orderId/:categoryId/:productId',
                            name: 'editOrderedProduct',
                            props: true,
                            component: ProductDetails,
                            meta: { title: 'Edit ordered product', cart: true },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'checkout',
                    name: 'shopCheckout',
                    props: true,
                    component: CheckoutOrder,
                    meta: { title: 'Checkout', cart: false },
                  },
                  {
                    path: 'confirmation',
                    name: 'shopOrderConfirmation',
                    props: true,
                    component: OrderConfirmation,
                    meta: { title: 'Order Confirmation', cart: false },
                  },
                  {
                    path: 'category/:productCategoryId',
                    props: true,
                    component: ProductsDataProvider,
                    children: [
                      {
                        path: '',
                        name: 'shopProductCategoryOverview',
                        props: true,
                        component: CategoryProductList,
                        meta: { title: 'Products in this category', cart: true, callout: true },
                      },
                      {
                        path: 'product/:productId',
                        name: 'shopProductDetails',
                        props: true,
                        component: ProductDetails,
                        meta: { title: 'Details about this product', cart: true, callout: true },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/dashboard',
        name: 'dashboard',
        component: DashboardSection,
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: NotificationCenter,
        meta: {
          isPublic: false,
          featureFlag: FEATURE_FLAG.NOTIFICATIONS.CENTER_RELEASE,
        },
      },
      {
        path: '/users',
        component: UsersDataProvider,
        meta: {
          isPublic: false,
        },
        children: [
          {
            path: '',
            name: 'userList',
            props: true,
            component: UserList,
          },
          {
            path: 'create',
            name: 'createUser',
            props: true,
            component: CreateUser,
          },
          {
            path: ':id/edit',
            name: 'editUser',
            props: true,
            component: EditUser,
          },
        ],
      },
      {
        path: 'booth/:boothId/print',
        name: 'boothPrint',
        props: true,
        component: BoothOrderSummary,
        meta: {
          noLayout: true,
        },
      },
      {
        path: '/documents',
        component: BookingsDataProvider,
        children: [
          {
            path: '',
            name: 'documents',
            props: true,
            component: DocumentList,
          },
          {
            path: 'archive',
            name: 'documentsArchive',
            props: true,
            component: DocumentList,
          },
          {
            path: ':signableId',
            props: true,
            component: DocumentDataProvider,
            children: [
              {
                path: '',
                name: 'documentDetails',
                props: true,
                component: DocumentDetails,
              },
            ],
          },
        ],
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/speakers',
        component: SpeakersDataProvider,
        meta: {
          hasSubmenu: true,
        },
        children: [
          {
            path: '',
            name: 'speakerList',
            props: true,
            component: SpeakerList,
          },
          {
            path: 'create',
            name: 'createSpeaker',
            props: true,
            component: CreateSpeaker,
          },
          {
            path: ':speakerId',
            props: true,
            component: SpeakerDataProvider,
            children: [
              {
                path: '',
                name: 'viewSpeaker',
                props: true,
                component: SpeakerView,
              },
              {
                path: 'edit',
                name: 'editSpeaker',
                props: true,
                component: EditSpeaker,
              },
              {
                path: 'events',
                name: 'editSpeakerEvents',
                props: true,
                component: EditSpeakerEvents,
              },
            ],
          },
        ],
      },
      reviewsAgenciesRoutes,
      reviewsProductsRoutes,
      {
        path: '/tickets/:selector?',
        component: EventsDataProvider,
        name: 'tickets',
        props: true,
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/events/:selector?',
        component: EventsDataProvider,
        name: 'events',
        props: true,
        meta: {
          isPublic: false,
          hasSubmenu: true,
        },
      },
      {
        path: '/events/:eventId/invitations',
        component: EventInvitations,
        name: 'invitations',
        props: true,
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/events/:eventId/edit',
        component: EventForm,
        name: 'eventEdit',
        props: true,
        meta: {
          isPublic: false,
          hasSubmenu: true,
        },
      },
      {
        path: '/event/:eventId',
        component: EventDataProvider,
        name: 'eventView',
        props: true,
        meta: {
          isPublic: false,
        },
        children: [
          {
            path: 'attendees',
            name: 'EventAttendees',
            props: true,
            component: EventApplications,
          },
          {
            path: 'attendees/upload',
            name: 'AttendeeUpload',
            props: true,
            component: AttendeeUpload,
          },
        ],
      },
      {
        path: '/events/invitations/:invitationQuotaId',
        component: InvitationDataProvider,
        props: true,
        children: [
          {
            path: '',
            name: 'invitationQuota',
            props: true,
            component: InvitationQuotaViewSwitch,
            children: [
              {
                path: 'ticket/new',
                props: true,
                name: 'invitationQuotaCreateTicket',
                component: CreateTicketModal,
              },
              {
                path: 'ticket/:ticketId',
                props: true,
                component: TicketDataProvider,
                children: [
                  {
                    path: 'show',
                    name: 'invitationQuotaTicketDetails',
                    props: true,
                    component: TicketDetailsModal,
                  },
                  {
                    path: 'edit',
                    name: 'invitationQuotaEditTicket',
                    props: true,
                    component: EditTicketModal,
                  },
                ],
              },
            ],
          },
          {
            path: 'upload',
            name: 'invitationQuotaCsvUpload',
            props: true,
            component: InvitationQuotaCsvUpload,
          },
          {
            path: 'match/:uploadId',
            name: 'invitationQuotaCsvMappingForm',
            props: true,
            component: InvitationQuotaCsvMappingForm,
          },
        ],
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/partner-company',
        component: PartnerCompany,
        children: [
          {
            path: '',
            name: 'partnerCompany',
            props: true,
            component: PartnerCompanyDetails,
          },
          {
            path: 'edit',
            name: 'partnerCompanyEdit',
            props: true,
            component: PartnerCompanyForm,
          },
        ],
        meta: {
          isPublic: false,
        },
      },
      {
        path: '/content/:slug',
        name: 'contentPage',
        component: LandingPage,
        meta: {
          hasSubmenu: true,
        },
      },
      {
        path: '/leads',
        name: 'leads',
        meta: {
          hasSubmenu: true,
        },
        children: [
          {
            path: 'myleads',
            component: MyLeads,
            name: 'myleads',
            children: [
              {
                path: ':slug',
                name: 'eventLeads',
                props: true,
                component: EventLeads,
              },
            ],
          },
          {
            path: 'recruiting-plus',
            name: 'recruitingPlus',
            props: true,
            component: HrReach,
          },
          {
            path: 'prospects',
            component: ProspectsSection,
            props: true,
            children: [
              {
                path: '',
                name: 'prospectEventsIndex',
                redirect: { name: 'prospectEvents', params: { selector: 'current' } },
              },
              {
                path: ':selector(current|archive)',
                name: 'prospectEvents',
                props: true,
                component: ProspectEvents,
              },
              {
                path: ':selector(current|archive)/:slug',
                name: 'prospectEvent',
                props: true,
                component: ProspectEvent,
                children: [
                  {
                    path: 'catalog',
                    name: 'prospectsCatalog',
                    props: true,
                    component: ProspectsCatalog,
                  },
                  {
                    path: 'your-prospects',
                    name: 'prospectLists',
                    props: true,
                    component: ProspectLists,
                    children: [
                      {
                        path: ':listId',
                        name: 'prospectList',
                        props: true,
                        component: ProspectList,
                        children: [
                          {
                            path: 'delete',
                            name: 'deleteProspectList',
                            props: true,
                            component: DeleteProspectListModal,
                          },
                          {
                            path: 'edit',
                            name: 'editProspectList',
                            props: true,
                            component: EditProspectListModal,
                          },
                          {
                            path: 'test',
                            name: 'sendProspectListTest',
                            props: true,
                            component: SendProspectTestEmailModal,
                          },
                          {
                            path: 'send-invitations',
                            name: 'sendProspectListInvitations',
                            props: true,
                            component: SendProspectListInvitationsModal,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
            meta: {
              isPublic: false,
            },
          },
        ],
      },
      {
        path: 'networking',
        meta: {
          hasSubmenu: true,
        },
        children: [
          {
            path: '',
            name: 'visitorProfilesList',
            component: VisitorProfiles,
            meta: { featureFlag: FEATURE_FLAG.FESTIVAL.IS_NETWORKING_ENABLED },
          },
          {
            path: ':tabId',
            name: 'visitorProfilesListTab',
            component: VisitorProfiles,
            props: true,
            meta: { featureFlag: FEATURE_FLAG.FESTIVAL.IS_NETWORKING_ENABLED },
          },
          {
            path: 'meetings',
            name: 'meetings',
            component: MeetingsContainer,
            meta: { fullWidth: true, featureFlag: FEATURE_FLAG.FESTIVAL.IS_MEETINGS_ENABLED },
            children: [
              {
                path: '',
                name: 'meetingsMyMeetings',
                component: MeetingsMyMeetings,
              },
              {
                path: 'requests',
                name: 'meetingsRequests',
                component: MeetingsRequests,
              },
              {
                path: 'declined',
                name: 'meetingsDeclined',
                component: MeetingsDeclined,
              },
              {
                path: 'configuration',
                name: 'meetingsConfiguration',
                component: MeetingsConfiguration,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/network-issue',
    name: 'network-issue',
    component: NetworkIssue,
    meta: {
      isPublic: true,
    },
  },

  {
    path: '/401',
    name: '401',
    component: ErrorUnauthorized,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/404',
    name: '404',
    component: ErrorNotFound,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
  {
    path: '/content/public/:slug',
    name: 'publicContentPage',
    component: LandingPage,
  },
] as const

export default routes
